import * as R from 'remeda';
import {
  type GetEffectiveDimensions,
  identityDimensions,
  type PageId,
} from '../coordinates/geometry';
import type { PageState } from '../store/types';
import type {
  Band,
  DocumentLayout,
  PageDimensions,
  PageLayoutData,
} from './types';

export type { Band, DocumentLayout, PageDimensions, PageLayoutData };

const PAGE_SPACING = 50;

export type LayoutBandHeights = {
  header?: number;
  beforePage?: number;
  afterPage?: number;
};

type CalculatePageLayoutParams = {
  pageId: PageId;
  pageState: PageState;
  contentWidth: number;
  currentY: number;
  getEffectiveDimensions: GetEffectiveDimensions;
  beforePageHeight: number;
  afterPageHeight: number;
};

const calculatePageLayout = ({
  pageId,
  pageState,
  contentWidth,
  currentY,
  getEffectiveDimensions,
  beforePageHeight,
  afterPageHeight,
}: CalculatePageLayoutParams): { layout: PageLayoutData; nextY: number } => {
  const effectiveDims = getEffectiveDimensions(pageId, pageState.dimensions);

  const x = (contentWidth - effectiveDims.width) / 2;

  let y = currentY;

  const beforeBand: Band | undefined =
    beforePageHeight > 0
      ? { x, y, width: effectiveDims.width, height: beforePageHeight }
      : undefined;
  y += beforePageHeight;

  const pageY = y;
  y += effectiveDims.height;

  const afterBand: Band | undefined =
    afterPageHeight > 0
      ? { x, y, width: effectiveDims.width, height: afterPageHeight }
      : undefined;
  y += afterPageHeight;

  const layout: PageLayoutData = {
    x,
    y: pageY,
    width: effectiveDims.width,
    height: effectiveDims.height,
    pageId,
    originalWidth: pageState.dimensions.width,
    originalHeight: pageState.dimensions.height,
    ...(beforeBand ? { beforeBand } : {}),
    ...(afterBand ? { afterBand } : {}),
  };

  return {
    layout,
    nextY: y + PAGE_SPACING,
  };
};

const getPageWidth =
  (
    pages: Record<PageId, PageState>,
    getEffectiveDimensions: GetEffectiveDimensions
  ) =>
  (pageId: PageId) => {
    const pageState = pages[pageId];
    if (!pageState) return 0;
    return getEffectiveDimensions(pageId, pageState.dimensions).width;
  };

export type CalculateLayoutOptions = {
  getEffectiveDimensions?: GetEffectiveDimensions;
  bands?: LayoutBandHeights;
};

export const calculateLayout = (
  pageOrder: PageId[],
  pages: Record<PageId, PageState>,
  options: CalculateLayoutOptions = {}
): DocumentLayout => {
  const { getEffectiveDimensions = identityDimensions, bands = {} } = options;
  const {
    header: headerHeight = 0,
    beforePage: beforePageHeight = 0,
    afterPage: afterPageHeight = 0,
  } = bands;

  return pageOrder.length === 0
    ? { pageLayouts: [], contentWidth: 0, contentHeight: 0 }
    : R.pipe(
        pageOrder,
        R.map(getPageWidth(pages, getEffectiveDimensions)),
        widths => Math.max(...widths),
        contentWidth =>
          R.pipe(
            pageOrder,
            R.reduce(
              (acc, pageId) => {
                const pageState = pages[pageId];
                if (!pageState) {
                  throw new Error(`Page state not found for page ${pageId}`);
                }

                const { layout, nextY } = calculatePageLayout({
                  pageId,
                  pageState,
                  contentWidth,
                  currentY: acc.finalY,
                  getEffectiveDimensions,
                  beforePageHeight,
                  afterPageHeight,
                });

                return {
                  pageLayouts: [...acc.pageLayouts, layout],
                  finalY: nextY,
                };
              },
              { pageLayouts: [] as PageLayoutData[], finalY: headerHeight }
            ),
            ({ pageLayouts, finalY }) => ({
              pageLayouts,
              contentWidth,
              contentHeight: finalY - PAGE_SPACING,
              ...(headerHeight > 0
                ? {
                    headerBand: {
                      x: 0,
                      y: 0,
                      width: contentWidth,
                      height: headerHeight,
                    },
                  }
                : {}),
            })
          )
      );
};
